<script setup>
const { $clientPosthog } = useNuxtApp()
const route = useRoute()
const themeStore = useThemeStore()
const title = computed(() => {
  if (route.path === '/privacy-policy') {
    return 'TARC | Privacy Policy'
  } else if (route.path === '/terms-of-service') {
    return 'TARC | Terms of Use and Terms of Service.'
  }

  return 'TARC'
})
const description = computed(() => {
  if (route.path === '/privacy-policy') {
    return 'TARC Privacy Policy.'
  } else if (route.path === '/terms-of-service') {
    return 'TARC Terms of Use and Terms of Service.'
  }

  return 'Preserving what matters, and elevating what\'s possible.'
})

useHead({
  title: title,
  htmlAttrs: {
    lang: 'en',
  },
  meta: [
    {
      name: 'description',
      content: description,
    },
    { charset: 'utf-8' },
    {
      name: 'viewport',
      content:
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0',
    },
    {
      key: 'theme-color',
      name: 'theme-color',
      content: computed(() => (themeStore.isDark ? '#060505' : '#ffffff')),
    },
    {
      'http-equiv': 'Content-Security-Policy',
      'content': Object.entries({
        'default-src': ['\'self\''],
        'script-src': ['\'self\'', '\'unsafe-inline\'', '\'unsafe-eval\''],
        'script-src-elem': [
          '\'self\'',
          '\'unsafe-inline\'',
          'https://us-assets.i.posthog.com',
        ],
        'style-src': ['\'self\'', '\'unsafe-inline\''],
        'font-src': ['\'self\''],
        'img-src': ['\'self\'', 'data:', '*'],
        'media-src': ['\'self\'', 'data:', 'blob:'],
        'connect-src': [
          '\'self\'',
          ...(import.meta.dev ? ['ws://localhost:4000/ws'] : []),
          'https://api.iconify.design',
          'https://us.i.posthog.com',
          'https://us-assets.i.posthog.com',
        ],
        'worker-src': ['\'self\'', 'blob:'],
      })
        .reduce((acc, [key, value]) => {
          acc.push(`${key} ${value.join(' ')}`)
          return acc
        }, [])
        .join('; '),
    },
  ],
  link: [
    {
      rel: 'manifest',
      href: '/site.webmanifest',
    },
    {
      rel: 'apple-touch-icon',
      sizes: '180x180',
      href: '/apple-touch-icon.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      href: '/android-chrome-192x192.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '16x16',
      href: '/favicon-16x16.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '32x32',
      href: '/favicon-32x32.png',
    },
  ],
})

if (import.meta.server) {
  useSeoMeta({
    description: description,
    ogType: 'website',
    ogTitle: title,
    ogDescription: description,
    ogImage: {
      type: 'image/png',
      url: 'https://tarc.ltd/social-card.png',
      alt: 'TARC',
      width: 1200,
      height: 630,
    },
    ogUrl: 'https://tarc.ltd',
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: {
      type: 'image/png',
      url: 'https://tarc.ltd/social-card.png',
      alt: 'TARC',
      width: 1200,
      height: 630,
    },
    twitterCard: 'summary_large_image',
  })
}

useEventListener('error', (error) => {
  $clientPosthog?.captureException(error)
})

useEventListener('unhandledrejection', (error) => {
  $clientPosthog?.captureException(error)
})
</script>

<template>
  <UApp>
    <NuxtLayout>
      <NuxtPage />
      <div
        class="hidden"
        aria-hidden="true"
      >
        Copyright © {{ new Date().getFullYear() }}, TARC. All rights reserved.
      </div>
      <UFooter class="mb-16 mt-4">
        <template #left>
          <p class="text-(--ui-text-muted) text-sm">
            Copyright © {{ new Date().getFullYear() }},
            <NuxtLink
              to="/"
              target="_blank"
            >
              TARC
            </NuxtLink>
          </p>
        </template>
        <UNavigationMenu
          :items="[
            {
              label: 'Terms of Service',
              to: '/terms-of-service',
            },
            {
              label: 'Privacy Policy',
              to: '/privacy-policy',
            },
          ]"
          variant="link"
        />
        <template #right>
          <UButton
            icon="simple-icons:facebook"
            color="neutral"
            variant="ghost"
            to="https://www.facebook.com/tarcltd/"
            target="_blank"
            class="rounded-full"
          />
          <UColorModeButton
            color="neutral"
            variant="ghost"
            class="rounded-full"
          />
        </template>
      </UFooter>
    </NuxtLayout>
  </UApp>
</template>
